import React from 'react';
import CustomTag from 'src/models/accounting-calendar/CustomTag';
import { CalendarDay } from 'src/models/accounting-calendar/GetCalendarResult';
import './AccountingCalendar.scss';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface GridCell {
    date: Date;
    inMonth: boolean;
}

/** Builds the Sun-Sat grid cells covering the given month, including leading/trailing fillers. */
export function buildMonthCells(year: number, month: number): GridCell[] {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    // JS getDay(): 0=Sun..6=Sat — matches our column order directly.
    const leading = first.getDay();
    const cells: GridCell[] = [];
    for (let i = 0; i < leading; i++) {
        const d = new Date(year, month - 1, 1 - (leading - i));
        cells.push({ date: d, inMonth: false });
    }
    for (let day = 1; day <= last.getDate(); day++) {
        cells.push({ date: new Date(year, month - 1, day), inMonth: true });
    }
    // Trailing fillers to complete the final week.
    while (cells.length % 7 !== 0) {
        const lastCell = cells[cells.length - 1].date;
        cells.push({ date: new Date(lastCell.getFullYear(), lastCell.getMonth(), lastCell.getDate() + 1), inMonth: false });
    }
    return cells;
}

function toIsoDate(d: Date): string {
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
}

interface Props {
    year: number;
    month: number; // 1-12
    days: CalendarDay[]; // CalendarDay entries for this month, from GET /calendar
    todayIso: string;
    onSelectTag: (tag: CustomTag) => void;
    /** Called when the user clicks "+N more" on a day with more than 1 tag. */
    onViewMore: (dateIso: string, tags: CustomTag[]) => void;
}

export default function AccountingCalendarGrid(props: Props) {
    const { year, month, days, todayIso, onSelectTag, onViewMore } = props;

    // Index the response days by their date string for quick lookup.
    const byDate: { [iso: string]: CalendarDay } = {};
    (days || []).forEach(d => { byDate[d.date] = d; });

    const cells = buildMonthCells(year, month);

    return (
        <div data-testid="acctCalendarGrid">
            <div className="acctCalendarGrid">
                {WEEKDAYS.map(w => (
                    <div key={w} className="acctCalendarWeekdayHeader">{w}</div>
                ))}
                {cells.map(cell => {
                    const iso = toIsoDate(cell.date);
                    // Look up data for every cell (including prev/next-month spillover) so the
                    // close-window days that fall outside this calendar month still show labels/tags.
                    const dayData = byDate[iso];
                    const isToday = iso === todayIso;
                    const dayOfWeek = cell.date.getDay();
                    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                    const classNames = ['acctCalendarCell'];
                    if (!cell.inMonth) {
                        classNames.push('acctCalendarCellOutside');
                    }
                    if (isToday) {
                        classNames.push('acctCalendarCellToday');
                    }
                    if (isWeekend) {
                        classNames.push('acctCalendarCellWeekend');
                    }
                    return (
                        <div key={iso} className={classNames.join(' ')} data-testid={`cell-${iso}`}>
                            <div className="acctCalendarDateNumber">{cell.date.getDate()}</div>
                            {dayData && dayData.mecDay !== undefined && dayData.mecDay !== null && (
                                <div className={`acctCalendarDayLabel ${dayData.mecDay > 0 ? 'acctCalendarDayLabelInWindow' : 'acctCalendarDayLabelPostClose'}`}>
                                    (Day {dayData.mecDay})
                                </div>
                            )}
                            {dayData && dayData.tags && dayData.tags.slice(0, 1).map(tag => (
                                <div
                                    key={tag.tagId}
                                    className={`acctCalendarTagChip ${tag.isRecurring ? 'acctCalendarTagChipRecurring' : 'acctCalendarTagChipOneTime'}`}
                                    title={tag.tagEventName}
                                    data-testid={`tag-${tag.tagId}`}
                                    onClick={() => onSelectTag(tag)}
                                >
                                    {tag.tagEventName}
                                </div>
                            ))}
                            {dayData && dayData.tags && dayData.tags.length > 1 && (
                                <div
                                    className="acctCalendarViewMoreLink"
                                    data-testid={`viewMore-${iso}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onViewMore(iso, dayData.tags);
                                    }}
                                >
                                    +{dayData.tags.length - 1} more
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
